import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

export type ColorTheme = 
  'rose' | 'lavender' | 'mint' | 'peach' | 'sky' |
  'ruby' | 'emerald' | 'sapphire' | 'amethyst' | 'amber' |
  'burgundy' | 'navy' | 'forest' | 'gold' | 'silver';

export interface ThemeClasses {
  primaryText: string;
  secondaryText: string;
  inputText: string; // Added for form inputs
  primaryBg: string;
  secondaryBg: string;
  accentBg: string;
  cardBg: string;
  primaryBorder: string;
  primaryButton: string;
  primaryButtonHover: string;
  secondaryButton: string;
  secondaryButtonHover: string;
  altSectionBg: string;
  success: string;
  warning: string;
  error: string;
  heroOverlay: string;
  featuredIconBg: string;
}

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
  themeClasses: ThemeClasses;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const getThemeClasses = (theme: ColorTheme, isDark: boolean): ThemeClasses => {
  const baseClasses = {
    success: isDark ? 'text-green-400' : 'text-green-600',
    warning: isDark ? 'text-yellow-400' : 'text-yellow-600',
    error: isDark ? 'text-red-400' : 'text-red-600',
    cardBg: isDark ? 'bg-gray-800' : 'bg-white',
    altSectionBg: isDark ? 'bg-gray-900' : 'bg-gray-50',
    inputText: isDark ? 'text-gray-200' : 'text-gray-900', // Default input text
  };
  
  const themeSpecific: Record<ColorTheme, Partial<ThemeClasses>> = {
    rose: {
      primaryText: isDark ? 'text-rose-400' : 'text-rose-600',
      secondaryText: isDark ? 'text-rose-300' : 'text-rose-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-rose-900' : 'bg-rose-500',
      secondaryBg: isDark ? 'bg-rose-800' : 'bg-rose-100',
      accentBg: isDark ? 'bg-rose-400' : 'bg-rose-200',
      primaryBorder: isDark ? 'border-rose-700' : 'border-rose-300',
      primaryButton: isDark ? 'bg-rose-700 text-white' : 'bg-rose-600 text-white',
      primaryButtonHover: isDark ? 'bg-rose-800' : 'bg-rose-700',
      secondaryButton: isDark ? 'bg-rose-200 text-rose-900' : 'bg-rose-100 text-rose-800',
      secondaryButtonHover: isDark ? 'bg-rose-300' : 'bg-rose-200',
      heroOverlay: 'bg-rose-900 bg-opacity-60',
      featuredIconBg: isDark ? 'bg-rose-800' : 'bg-rose-100 text-rose-600',
    },
    lavender: {
      primaryText: isDark ? 'text-purple-300' : 'text-purple-600',
      secondaryText: isDark ? 'text-purple-200' : 'text-purple-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-purple-900' : 'bg-purple-400',
      secondaryBg: isDark ? 'bg-purple-800' : 'bg-purple-100',
      accentBg: isDark ? 'bg-purple-300' : 'bg-purple-200',
      primaryBorder: isDark ? 'border-purple-700' : 'border-purple-300',
      primaryButton: isDark ? 'bg-purple-700 text-white' : 'bg-purple-500 text-white',
      primaryButtonHover: isDark ? 'bg-purple-800' : 'bg-purple-600',
      secondaryButton: isDark ? 'bg-purple-200 text-purple-900' : 'bg-purple-100 text-purple-800',
      secondaryButtonHover: isDark ? 'bg-purple-300' : 'bg-purple-200',
      heroOverlay: 'bg-purple-900 bg-opacity-60',
      featuredIconBg: isDark ? 'bg-purple-800' : 'bg-purple-100 text-purple-600',
    },
    mint: {
      primaryText: isDark ? 'text-green-300' : 'text-green-600',
      secondaryText: isDark ? 'text-green-200' : 'text-green-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-green-900' : 'bg-green-400',
      secondaryBg: isDark ? 'bg-green-800' : 'bg-green-100',
      accentBg: isDark ? 'bg-green-300' : 'bg-green-200',
      primaryBorder: isDark ? 'border-green-700' : 'border-green-300',
      primaryButton: isDark ? 'bg-green-700 text-white' : 'bg-green-500 text-white',
      primaryButtonHover: isDark ? 'bg-green-800' : 'bg-green-600',
      secondaryButton: isDark ? 'bg-green-200 text-green-900' : 'bg-green-100 text-green-800',
      secondaryButtonHover: isDark ? 'bg-green-300' : 'bg-green-200',
      heroOverlay: 'bg-green-900 bg-opacity-60',
      featuredIconBg: isDark ? 'bg-green-800' : 'bg-green-100 text-green-600',
    },
    peach: {
      primaryText: isDark ? 'text-orange-300' : 'text-orange-600',
      secondaryText: isDark ? 'text-orange-200' : 'text-orange-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-orange-900' : 'bg-orange-400',
      secondaryBg: isDark ? 'bg-orange-800' : 'bg-orange-100',
      accentBg: isDark ? 'bg-orange-300' : 'bg-orange-200',
      primaryBorder: isDark ? 'border-orange-700' : 'border-orange-300',
      primaryButton: isDark ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white',
      primaryButtonHover: isDark ? 'bg-orange-800' : 'bg-orange-600',
      secondaryButton: isDark ? 'bg-orange-200 text-orange-900' : 'bg-orange-100 text-orange-800',
      secondaryButtonHover: isDark ? 'bg-orange-300' : 'bg-orange-200',
      heroOverlay: 'bg-orange-900 bg-opacity-60',
      featuredIconBg: isDark ? 'bg-orange-800' : 'bg-orange-100 text-orange-600',
    },
    sky: {
      primaryText: isDark ? 'text-sky-300' : 'text-sky-600',
      secondaryText: isDark ? 'text-sky-200' : 'text-sky-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-sky-900' : 'bg-sky-400',
      secondaryBg: isDark ? 'bg-sky-800' : 'bg-sky-100',
      accentBg: isDark ? 'bg-sky-300' : 'bg-sky-200',
      primaryBorder: isDark ? 'border-sky-700' : 'border-sky-300',
      primaryButton: isDark ? 'bg-sky-700 text-white' : 'bg-sky-500 text-white',
      primaryButtonHover: isDark ? 'bg-sky-800' : 'bg-sky-600',
      secondaryButton: isDark ? 'bg-sky-200 text-sky-900' : 'bg-sky-100 text-sky-800',
      secondaryButtonHover: isDark ? 'bg-sky-300' : 'bg-sky-200',
      heroOverlay: 'bg-sky-900 bg-opacity-60',
      featuredIconBg: isDark ? 'bg-sky-800' : 'bg-sky-100 text-sky-600',
    },
    ruby: {
      primaryText: isDark ? 'text-red-300' : 'text-red-700',
      secondaryText: isDark ? 'text-red-200' : 'text-red-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-red-900' : 'bg-red-600',
      secondaryBg: isDark ? 'bg-red-800' : 'bg-red-100',
      accentBg: isDark ? 'bg-red-300' : 'bg-red-200',
      primaryBorder: isDark ? 'border-red-700' : 'border-red-400',
      primaryButton: isDark ? 'bg-red-700 text-white' : 'bg-red-600 text-white',
      primaryButtonHover: isDark ? 'bg-red-800' : 'bg-red-700',
      secondaryButton: isDark ? 'bg-red-200 text-red-900' : 'bg-red-100 text-red-800',
      secondaryButtonHover: isDark ? 'bg-red-300' : 'bg-red-200',
      heroOverlay: 'bg-red-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-red-800' : 'bg-red-600 text-white',
    },
    emerald: {
      primaryText: isDark ? 'text-emerald-300' : 'text-emerald-700',
      secondaryText: isDark ? 'text-emerald-200' : 'text-emerald-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-emerald-900' : 'bg-emerald-600',
      secondaryBg: isDark ? 'bg-emerald-800' : 'bg-emerald-100',
      accentBg: isDark ? 'bg-emerald-300' : 'bg-emerald-200',
      primaryBorder: isDark ? 'border-emerald-700' : 'border-emerald-400',
      primaryButton: isDark ? 'bg-emerald-700 text-white' : 'bg-emerald-600 text-white',
      primaryButtonHover: isDark ? 'bg-emerald-800' : 'bg-emerald-700',
      secondaryButton: isDark ? 'bg-emerald-200 text-emerald-900' : 'bg-emerald-100 text-emerald-800',
      secondaryButtonHover: isDark ? 'bg-emerald-300' : 'bg-emerald-200',
      heroOverlay: 'bg-emerald-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-emerald-800' : 'bg-emerald-600 text-white',
    },
    sapphire: {
      primaryText: isDark ? 'text-blue-300' : 'text-blue-700',
      secondaryText: isDark ? 'text-blue-200' : 'text-blue-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-blue-900' : 'bg-blue-600',
      secondaryBg: isDark ? 'bg-blue-800' : 'bg-blue-100',
      accentBg: isDark ? 'bg-blue-300' : 'bg-blue-200',
      primaryBorder: isDark ? 'border-blue-700' : 'border-blue-400',
      primaryButton: isDark ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white',
      primaryButtonHover: isDark ? 'bg-blue-800' : 'bg-blue-700',
      secondaryButton: isDark ? 'bg-blue-200 text-blue-900' : 'bg-blue-100 text-blue-800',
      secondaryButtonHover: isDark ? 'bg-blue-300' : 'bg-blue-200',
      heroOverlay: 'bg-blue-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-blue-800' : 'bg-blue-600 text-white',
    },
    amethyst: {
      primaryText: isDark ? 'text-fuchsia-300' : 'text-fuchsia-700',
      secondaryText: isDark ? 'text-fuchsia-200' : 'text-fuchsia-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-fuchsia-900' : 'bg-fuchsia-600',
      secondaryBg: isDark ? 'bg-fuchsia-800' : 'bg-fuchsia-100',
      accentBg: isDark ? 'bg-fuchsia-300' : 'bg-fuchsia-200',
      primaryBorder: isDark ? 'border-fuchsia-700' : 'border-fuchsia-400',
      primaryButton: isDark ? 'bg-fuchsia-700 text-white' : 'bg-fuchsia-600 text-white',
      primaryButtonHover: isDark ? 'bg-fuchsia-800' : 'bg-fuchsia-700',
      secondaryButton: isDark ? 'bg-fuchsia-200 text-fuchsia-900' : 'bg-fuchsia-100 text-fuchsia-800',
      secondaryButtonHover: isDark ? 'bg-fuchsia-300' : 'bg-fuchsia-200',
      heroOverlay: 'bg-fuchsia-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-fuchsia-800' : 'bg-fuchsia-600 text-white',
    },
    amber: {
      primaryText: isDark ? 'text-amber-300' : 'text-amber-700',
      secondaryText: isDark ? 'text-amber-200' : 'text-amber-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-amber-900' : 'bg-amber-600',
      secondaryBg: isDark ? 'bg-amber-800' : 'bg-amber-100',
      accentBg: isDark ? 'bg-amber-300' : 'bg-amber-200',
      primaryBorder: isDark ? 'border-amber-700' : 'border-amber-400',
      primaryButton: isDark ? 'bg-amber-700 text-white' : 'bg-amber-600 text-white',
      primaryButtonHover: isDark ? 'bg-amber-800' : 'bg-amber-700',
      secondaryButton: isDark ? 'bg-amber-200 text-amber-900' : 'bg-amber-100 text-amber-800',
      secondaryButtonHover: isDark ? 'bg-amber-300' : 'bg-amber-200',
      heroOverlay: 'bg-amber-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-amber-800' : 'bg-amber-600 text-white',
    },
    burgundy: {
      primaryText: isDark ? 'text-red-300' : 'text-red-800',
      secondaryText: isDark ? 'text-red-200' : 'text-red-900',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-red-900' : 'bg-red-800',
      secondaryBg: isDark ? 'bg-red-800' : 'bg-red-100',
      accentBg: isDark ? 'bg-red-300' : 'bg-red-200',
      primaryBorder: isDark ? 'border-red-700' : 'border-red-500',
      primaryButton: isDark ? 'bg-red-800 text-white' : 'bg-red-700 text-white',
      primaryButtonHover: isDark ? 'bg-red-900' : 'bg-red-800',
      secondaryButton: isDark ? 'bg-red-200 text-red-900' : 'bg-red-100 text-red-800',
      secondaryButtonHover: isDark ? 'bg-red-300' : 'bg-red-200',
      heroOverlay: 'bg-red-900 bg-opacity-80',
      featuredIconBg: isDark ? 'bg-red-900' : 'bg-red-800 text-white',
    },
    navy: {
      primaryText: isDark ? 'text-blue-300' : 'text-blue-800',
      secondaryText: isDark ? 'text-blue-200' : 'text-blue-900',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-blue-900' : 'bg-blue-800',
      secondaryBg: isDark ? 'bg-blue-800' : 'bg-blue-100',
      accentBg: isDark ? 'bg-blue-300' : 'bg-blue-200',
      primaryBorder: isDark ? 'border-blue-700' : 'border-blue-500',
      primaryButton: isDark ? 'bg-blue-800 text-white' : 'bg-blue-700 text-white',
      primaryButtonHover: isDark ? 'bg-blue-900' : 'bg-blue-800',
      secondaryButton: isDark ? 'bg-blue-200 text-blue-900' : 'bg-blue-100 text-blue-800',
      secondaryButtonHover: isDark ? 'bg-blue-300' : 'bg-blue-200',
      heroOverlay: 'bg-blue-900 bg-opacity-80',
      featuredIconBg: isDark ? 'bg-blue-900' : 'bg-blue-800 text-white',
    },
    forest: {
      primaryText: isDark ? 'text-green-300' : 'text-green-800',
      secondaryText: isDark ? 'text-green-200' : 'text-green-900',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-green-900' : 'bg-green-800',
      secondaryBg: isDark ? 'bg-green-800' : 'bg-green-100',
      accentBg: isDark ? 'bg-green-300' : 'bg-green-200',
      primaryBorder: isDark ? 'border-green-700' : 'border-green-500',
      primaryButton: isDark ? 'bg-green-800 text-white' : 'bg-green-700 text-white',
      primaryButtonHover: isDark ? 'bg-green-900' : 'bg-green-800',
      secondaryButton: isDark ? 'bg-green-200 text-green-900' : 'bg-green-100 text-green-800',
      secondaryButtonHover: isDark ? 'bg-green-300' : 'bg-green-200',
      heroOverlay: 'bg-green-900 bg-opacity-80',
      featuredIconBg: isDark ? 'bg-green-900' : 'bg-green-800 text-white',
    },
    gold: {
      primaryText: isDark ? 'text-yellow-300' : 'text-yellow-700',
      secondaryText: isDark ? 'text-yellow-200' : 'text-yellow-800',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-yellow-800' : 'bg-yellow-600',
      secondaryBg: isDark ? 'bg-yellow-900' : 'bg-yellow-100',
      accentBg: isDark ? 'bg-yellow-400' : 'bg-yellow-200',
      primaryBorder: isDark ? 'border-yellow-600' : 'border-yellow-400',
      primaryButton: isDark ? 'bg-yellow-700 text-white' : 'bg-yellow-600 text-white',
      primaryButtonHover: isDark ? 'bg-yellow-800' : 'bg-yellow-700',
      secondaryButton: isDark ? 'bg-yellow-200 text-yellow-900' : 'bg-yellow-100 text-yellow-800',
      secondaryButtonHover: isDark ? 'bg-yellow-300' : 'bg-yellow-200',
      heroOverlay: 'bg-yellow-900 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-yellow-800' : 'bg-yellow-600 text-white',
    },
    silver: {
      primaryText: isDark ? 'text-gray-300' : 'text-gray-600',
      secondaryText: isDark ? 'text-gray-200' : 'text-gray-700',
      inputText: isDark ? 'text-gray-200' : 'text-gray-900',
      primaryBg: isDark ? 'bg-gray-700' : 'bg-gray-500',
      secondaryBg: isDark ? 'bg-gray-800' : 'bg-gray-100',
      accentBg: isDark ? 'bg-gray-300' : 'bg-gray-200',
      primaryBorder: isDark ? 'border-gray-600' : 'border-gray-400',
      primaryButton: isDark ? 'bg-gray-600 text-white' : 'bg-gray-500 text-white',
      primaryButtonHover: isDark ? 'bg-gray-700' : 'bg-gray-600',
      secondaryButton: isDark ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-800',
      secondaryButtonHover: isDark ? 'bg-gray-300' : 'bg-gray-200',
      heroOverlay: 'bg-gray-800 bg-opacity-70',
      featuredIconBg: isDark ? 'bg-gray-700' : 'bg-gray-500 text-white',
    },
  };
  
  return {
    ...baseClasses,
    ...(themeSpecific[theme] as ThemeClasses),
  };
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  const [colorTheme, setColorTheme] = useState<ColorTheme>(() => {
    const saved = localStorage.getItem('colorTheme');
    return (saved as ColorTheme) || 'rose';
  });

  const themeClasses = getThemeClasses(colorTheme, darkMode);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme]);

  return (
    <ThemeContext.Provider value={{ 
      darkMode, 
      toggleDarkMode, 
      colorTheme, 
      setColorTheme,
      themeClasses
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;